import AddressInput from '@/gf/components/AddressInput'
import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Checkbox from '@/gf/components/Checkbox'
import Field from '@/gf/components/Field'
import Form from '@/gf/components/Form'
import PhoneInput from '@/gf/components/PhoneInput'
import useMsgs from '@/gf/hooks/useMsgs'
import * as api from '@/suppliers/modules/api'
import { useState } from 'react'
import ReactQuill from 'react-quill'
import type { Store } from '../../../types'
import useSession from '../../hooks/useSession'
import useUpdateStore from '../../hooks/useUpdateStore'
import SettingsM from '../../modules/Settings'
import StripeSetup from './Store/StripeSetup'
import StoreLogoAndBanner from './StoreLogoAndBanner'

const Store = ({ activeStore }: { activeStore: Store }) => {
  const { featureFlags } = useSession()
  const [_msgs, msgsMgr] = useMsgs()
  const [storeAddress, setStoreAddress] = useState(activeStore.address)
  const [storePhoneNumber, setStorePhoneNumber] = useState(activeStore.phoneNumber ?? '')
  const [enabled, setEnabled] = useState(activeStore.enabled)
  const [autoQuote, setAutoQuote] = useState(activeStore.autoQuote)
  const updateStore = useUpdateStore()
  const [form, setForm] = useState(SettingsM.toStorePolicyForm(activeStore))

  const saveStoreDetails = () => {
    const address = storeAddress as NonNullable<typeof storeAddress>

    Promise.all([
      api.updateStoreAddressSettings(activeStore.id, address, storePhoneNumber),
      updateStore({ storeId: activeStore.id, enabled, autoQuote }),
    ]).then(([updateAddressResp]) => {
      if (updateAddressResp.status === 204) {
        msgsMgr.add('Store details saved.', 'positive')
      } else {
        msgsMgr.addUnknownError()
      }
    })
  }

  const updatePolicies = async () => {
    const response = await api.updateStorePolicySettings(activeStore.id, form)
    const { status } = response

    if (status === 204) {
      msgsMgr.add('Store policy information updated!', 'positive')
    } else {
      msgsMgr.add('Something went wrong', 'negative')
    }
  }

  return (
    <div className="space-y-6">
      <Card title="Store Details">
        <Form onSubmit={saveStoreDetails}>
          <Card.Section title="Settings">
            <ul className="space-y-2">
              <li className="space-y-1">
                <Checkbox checked={enabled} setChecked={setEnabled}>
                  Storefront Enabled
                </Checkbox>
                <div className="text-sm text-gray-500">
                  Determines whether your storefront and products are available on Gearflow.com.
                </div>
              </li>
              {featureFlags.autoQuoteMpnMatch && (
                <li className="space-y-1">
                  <Checkbox checked={autoQuote} setChecked={setAutoQuote}>
                    Auto Quote
                  </Checkbox>
                  <div className="text-sm text-gray-500">
                    Determines whether Gearflow should use your current price to quote requests for
                    parts that you have in stock.
                  </div>
                </li>
              )}
            </ul>
          </Card.Section>
          <Card.Section title="Phone Number">
            <PhoneInput label="" value={storePhoneNumber} onChange={setStorePhoneNumber} />
          </Card.Section>
          <Card.Section title="Address">
            <div className="space-y-4">
              <AddressInput
                address={storeAddress}
                onChange={(newAddress) => setStoreAddress(newAddress)}
              />
            </div>
          </Card.Section>
          <div className="bg-gray-50 p-6 flex justify-end rounded-b-md">
            <Button type="submit" title="Save" />
          </div>
        </Form>
      </Card>
      <StoreLogoAndBanner />
      <Card title="Store Policies">
        <Form onSubmit={updatePolicies}>
          <Card.Section>
            <div className="space-y-4">
              <Field label="About Us" desc="Description of the store.">
                <ReactQuill
                  className="product-form-quill"
                  value={form.description}
                  onChange={(description) => setForm({ ...form, description })}
                />
              </Field>

              <Field label="Shipping Info." desc="Information about the stores shipping policy.">
                <ReactQuill
                  className="product-form-quill"
                  value={form.shippingInfo}
                  onChange={(shippingInfo) => setForm({ ...form, shippingInfo })}
                />
              </Field>

              <Field
                label="Return and Warranty Policies"
                desc="Information about the store's return and warranty policies."
              >
                <ReactQuill
                  className="product-form-quill"
                  value={form.returnPolicy}
                  onChange={(returnPolicy) => setForm({ ...form, returnPolicy })}
                />
              </Field>
            </div>
          </Card.Section>
          <div className="bg-gray-50 p-6 flex justify-end rounded-b-md">
            <Button type="submit" title="Save" />
          </div>
        </Form>
      </Card>

      <StripeSetup />
    </div>
  )
}

export default Store
